import { useState } from "react";
import { useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { getHelp } from "@/lib/helpContent";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetDescription,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { HelpCircle, BookOpen, Lightbulb, Phone, Mail } from "lucide-react";
import { toast } from "sonner";

export default function HelpButton() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const help = getHelp(location.pathname);

  const downloadManual = async () => {
    try {
      const res = await api.get("/admin/user-manual", { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = "manuel_amalvbc.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <button
          data-testid="help-button"
          aria-label="Aide contextuelle"
          className="fixed bottom-24 right-6 z-50 h-12 w-12 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg flex items-center justify-center transition-transform hover:scale-105"
        >
          <HelpCircle className="h-6 w-6" />
        </button>
      </SheetTrigger>
      <SheetContent side="right" className="w-full sm:max-w-md overflow-y-auto" data-testid="help-sheet">
        <SheetHeader>
          <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-emerald-600">
            Aide contextuelle
          </div>
          <SheetTitle className="font-barlow text-2xl tracking-tight">
            {help?.title || "Aide AMAL VBC"}
          </SheetTitle>
          {help?.intro && (
            <SheetDescription className="text-sm text-slate-600 leading-relaxed">
              {help.intro}
            </SheetDescription>
          )}
        </SheetHeader>

        {help ? (
          <div className="mt-6 space-y-6">
            {help.steps?.length > 0 && (
              <section>
                <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
                  Étapes
                </h3>
                <ol className="space-y-2">
                  {help.steps.map((s, i) => (
                    <li key={i} className="flex gap-3 text-sm text-slate-700 leading-relaxed">
                      <span className="h-5 w-5 rounded-full bg-emerald-100 text-emerald-700 font-bold text-xs flex items-center justify-center shrink-0 mt-0.5">
                        {i + 1}
                      </span>
                      <span dangerouslySetInnerHTML={{ __html: s }} />
                    </li>
                  ))}
                </ol>
              </section>
            )}

            {help.tips?.length > 0 && (
              <section className="bg-emerald-50 border border-emerald-200 rounded-md p-4">
                <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-emerald-700 mb-2">
                  <Lightbulb className="h-3.5 w-3.5" /> Astuce
                </div>
                {help.tips.map((t, i) => (
                  <p key={i} className="text-sm text-emerald-900 leading-relaxed">{t}</p>
                ))}
              </section>
            )}
          </div>
        ) : (
          <div className="mt-6 text-sm text-slate-500">
            Aucune aide spécifique pour cette page. Consultez le manuel complet ci-dessous.
          </div>
        )}

        <div className="mt-8 border-t border-slate-200 pt-6">
          <h3 className="text-xs font-bold uppercase tracking-wider text-slate-700 mb-3">
            Aller plus loin
          </h3>
          <Button onClick={downloadManual} variant="outline" className="w-full justify-start"
            data-testid="help-manual-button">
            <BookOpen className="h-4 w-4 mr-2" />
            Télécharger le manuel complet (22 pages)
          </Button>
        </div>

        <div className="mt-6 border-t border-slate-200 pt-6 text-xs text-slate-500 space-y-1.5">
          <div className="font-bold text-slate-700 uppercase tracking-wider mb-2">
            Support
          </div>
          <div className="flex items-center gap-2">
            <Mail className="h-3 w-3" /> amalvbc@yahoo.com
          </div>
          <div className="flex items-center gap-2">
            <Phone className="h-3 w-3" /> +229 01 97 57 95 82
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
